/** Bundled release notes — the source of truth for the "What's New" panel.
 *
 *  Entries are keyed by the app's semantic version (package.json `version`,
 *  surfaced at runtime as APP_BUILD_INFO.version). When you ship a release
 *  worth telling users about, bump package.json and add an entry to the TOP
 *  of RELEASE_NOTES (newest first). Notes ship inside the bundle, so they are
 *  always version-accurate — no backend, no DB, no separate publish step.
 *
 *  The WhatsNewSheet compares the running version against the last version the
 *  user has seen (localStorage) and shows everything in between, once. Keep the
 *  copy short and user-facing: no internal jargon, no PR numbers, no emoji. */

/** Feature switch — parked. While false, the "What's New" sheet never mounts
 *  (no auto-open, no on-demand open) and its "What's new" menu entry is hidden,
 *  on every device. Flip to true to re-enable. */
export const WHATS_NEW_ENABLED = false;

export type ReleaseNoteCategory = 'new' | 'improved' | 'fixed';

export interface ReleaseNoteItem {
  category: ReleaseNoteCategory;
  /** One short, plain-English line. What the user can now do, or what no
   *  longer breaks. Avoid implementation detail. */
  text: string;
}

export interface ReleaseNote {
  /** Must match a shipped package.json version exactly. */
  version: string;
  /** ISO date (YYYY-MM-DD) the release went out. */
  date: string;
  /** Optional one-line headline for the release. */
  title?: string;
  items: ReleaseNoteItem[];
}

/** Newest first. The first entry's version is treated as "latest". */
export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '0.3.0',
    date: '2026-06-12',
    title: 'Desktop app, push notifications and a smarter tender queue',
    items: [
      {
        category: 'new',
        text: 'Quantis is now available as a Mac desktop app, with native notifications, a dock badge and automatic updates.',
      },
      {
        category: 'new',
        text: 'Chat messages can now reach you as push notifications on all your devices, with full message previews and a mark as read action.',
      },
      {
        category: 'new',
        text: 'The tender queue now plans around your capacity. Incoming tenders get realistic lead times and promised dates, and dates that start to drift are flagged.',
      },
      {
        category: 'new',
        text: 'New Events hub for managing Instructions and Dayworks, including an inbox for events not yet linked to a project.',
      },
      {
        category: 'new',
        text: 'Mark up PDF drawings directly in the viewer, then save or export your annotations.',
      },
      {
        category: 'new',
        text: 'Takeoff summaries can now be exported to an Excel workbook.',
      },
      {
        category: 'improved',
        text: 'The Projects list and app shell have a cleaner, more native look.',
      },
      {
        category: 'improved',
        text: 'Documents are easier to scan, with clearer file cards, revision labels and live Google Drive folder names.',
      },
      {
        category: 'improved',
        text: 'Starting a new message now finds people reliably, and duplicate names no longer appear in the picker.',
      },
      {
        category: 'improved',
        text: 'Out of office time is shaded more clearly in the calendar day and week views.',
      },
      {
        category: 'fixed',
        text: "This What's New card now has a solid background so you can read it over busy pages.",
      },
      {
        category: 'fixed',
        text: 'Project timeline dates no longer revert after you edit them.',
      },
      {
        category: 'fixed',
        text: 'CSV imports into a bill of quantities no longer fail silently.',
      },
      {
        category: 'fixed',
        text: "Editing a user's details no longer moves them to a different workspace.",
      },
    ],
  },
  {
    version: '0.2.0',
    date: '2026-06-05',
    title: 'Takeoff improvements',
    items: [
      {
        category: 'new',
        text: 'You can now set a different height or width on a single measured line, so one wall run can be taller than the rest without changing the others.',
      },
      {
        category: 'new',
        text: 'New Hide all button in the Takeoff Types panel header hides every type at once so you can see the drawing clearly. Tap again to show them.',
      },
      {
        category: 'new',
        text: 'Added External walls and Internal walls to the group element list when you set up a takeoff type.',
      },
      {
        category: 'fixed',
        text: 'The Takeoff Summary now opens when you tap the summary button. It was rendering behind the drawing before.',
      },
      {
        category: 'fixed',
        text: 'Area outlines are thinner so they no longer cover the lines on your drawing, especially when zoomed in.',
      },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-06-05',
    title: "See what's changed",
    items: [
      {
        category: 'new',
        text: "After every update you'll now get a short summary of what's new and what we've fixed, instead of just a refresh prompt.",
      },
    ],
  },
];

/** Numeric-segment semver compare. Pre-release/build suffixes are ignored
 *  (e.g. "1.2.0-rc.1" compares as "1.2.0"). Returns -1, 0, or 1. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) =>
    v
      .split('-')[0]
      .split('.')
      .map((n) => Number.parseInt(n, 10) || 0);
  const pa = parse(a);
  const pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da > db ? 1 : -1;
  }
  return 0;
}

/** The latest documented version, or null if there are no notes. */
export function latestReleaseVersion(): string | null {
  return RELEASE_NOTES[0]?.version ?? null;
}

/** Notes strictly newer than `sinceVersion`, capped at `upToVersion`
 *  (inclusive) so we never show notes for a build the user isn't running yet.
 *  Pass `sinceVersion = null` to get every note up to the running version. */
export function releaseNotesBetween(
  sinceVersion: string | null,
  upToVersion: string,
): ReleaseNote[] {
  return RELEASE_NOTES.filter((note) => {
    const newerThanSeen = sinceVersion === null || compareVersions(note.version, sinceVersion) > 0;
    const notFromFuture = compareVersions(note.version, upToVersion) <= 0;
    return newerThanSeen && notFromFuture;
  });
}
